import type { FC } from 'react'
import React, { memo } from 'react'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import type { ITheme } from '@homeviews/styles'
import type { Review } from '@homeviews/models'
import { Card, Typography, StarRating, StatusLabel } from '@homeviews/shared-ui'
import {
  splitText,
  formatToMonthAndYear,
  ExcerptWithKeywords,
} from '@homeviews/utils'
import { REVIEWER_TYPES } from '@homeviews/statistics'
import { getExcerptIndexableData } from '../../helpers/getExcerptIndexableData'

const useStyles = makeStyles((theme: ITheme) =>
  createStyles({
    root: {
      border: '0.5px solid rgba(0,0,0,0.02)',
      borderRadius: theme.spacing(0.625),
      boxShadow: '0 15px 20px 0 rgba(46,81,123,0.04)',
      backgroundColor: theme.colors.white,
      padding: theme.spacing(2.5, 2.75),
      '&:hover': {
        cursor: 'pointer',
      },
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    statusWrapper: {
      flexGrow: 0,
    },
    meta: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'flex-start',
    },
    metaEl: {
      display: 'flex',
      alignItems: 'flex-start',
      letterSpacing: '0.17px',
      lineHeight: '24px',
      marginBottom: theme.spacing(1.25),
      marginRight: theme.spacing(2),
    },
    metaLeadText: {
      color: '#000433',
      fontWeight: 700,
      marginRight: theme.spacing(0.625),
    },
    metaText: {
      letterSpacing: '0.17px',
      lineHeight: '24px',
      color: theme.colors.grey500,
    },
    locationText: {
      color: '#000433',
    },
    body: {},
    contentText: {
      marginTop: theme.spacing(1.375),
    },
    icon: {
      width: '17px',
      marginRight: theme.spacing(0.5),
      marginTop: '3px',
    },
    locationIcon: {
      width: '10px',
      marginRight: theme.spacing(0.5),
      marginTop: '3px',
    },
    readMoreText: {
      color: '#00837d',
      fontWeight: 500,
    },
  }),
)
interface IProps extends Review {
  className?: string
  onClick?: (id: number) => void
  verified: number
  searchKeyword?: string
}

const ReviewCard: FC<IProps> = ({
  className,
  onClick,
  id,
  title,
  content,
  development_name,
  review_date,
  display_name,
  reviewer_type,
  status,
  rating,
  verified = 0,
  searchKeyword = '',
  ...rest
}) => {
  const classes = useStyles()
  const handleClick = () => {
    typeof onClick === 'function' && onClick(id)
  }

  const transformedContent = splitText(content, 220)

  let keywordsData = []

  if (searchKeyword) {
    keywordsData = getExcerptIndexableData({
      title,
      content,
      ...rest,
    })
  }

  return (
    <Card className={clsx(classes.root, className)} onClick={handleClick}>
      <div className={classes.header}>
        <div className={classes.meta}>
          <div className={classes.metaEl}>
            <StarRating value={rating} />
          </div>
          {verified === 1 ? (
            <div className={classes.metaEl}>
              <img
                className={classes.icon}
                src="assets/icons/small/verified.svg"
                alt="Location icon"
              />
              <Typography className={clsx(classes.metaLeadText)}>
                Verified Resident
              </Typography>
            </div>
          ) : null}
          <div className={classes.metaEl}>
            <img
              className={classes.locationIcon}
              src="assets/icons/location.svg"
              alt="Location icon"
            />
            <Typography
              className={clsx(classes.metaLeadText, classes.locationText)}
            >
              Reviewing {development_name}
            </Typography>
          </div>
          {!!display_name && (
            <div className={classes.metaEl}>
              <Typography className={classes.metaLeadText}>
                {display_name}
              </Typography>
              {!!reviewer_type && (
                <Typography className={classes.metaText}>
                  ({REVIEWER_TYPES?.[reviewer_type]?.label || reviewer_type})
                </Typography>
              )}
            </div>
          )}
          {!!review_date && (
            <div className={classes.metaEl}>
              <Typography className={classes.metaLeadText}>Date</Typography>
              <Typography className={classes.metaText}>
                {formatToMonthAndYear(review_date)}
              </Typography>
            </div>
          )}
        </div>
        <div className={classes.statusWrapper}>
          <StatusLabel status={status} />
        </div>
      </div>
      <div className={classes.body}>
        <Typography variant="h3">{title}</Typography>
        <Typography className={classes.contentText}>
          {transformedContent.truncated}
          <span className={classes.readMoreText}>Read the full review</span>
        </Typography>
      </div>
      <ExcerptWithKeywords data={keywordsData} keyword={searchKeyword} />
    </Card>
  )
}

function propsAreEqual(prevReview, nextReview) {
  return prevReview.id === nextReview.id
}

export default memo(ReviewCard, propsAreEqual)

// export default ReviewCard
